import { useEffect, useMemo, useState } from "react";
import api from "../services/api";
import { ADMIN_ROLES, ROLE_MODULE_ACCESS } from "../config/adminSopConfig";

/** Capability keys that deny these UI guard actions when missing. */
const ACTION_REQUIRED_PERMISSION = {
  change_permissions: ["change_permissions"],
  platform_settings: ["configure_platform_settings", "change_system_settings"],
  approve_payments: ["approve_payments"],
  suspend_users: ["suspend_users", "suspend_profiles"],
  modify_verification: ["modify_verification_status"],
};

export function resolveAdminRoleFromAuth() {
  const authRole = String(api.getAuthRoleFromToken?.() || "").toUpperCase();
  if (authRole === "ADMIN") return "SUPER_ADMIN";
  const token = api.getToken?.();
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      const explicit = String(payload?.admin_sop_role || "").trim().toUpperCase();
      if (explicit) return explicit;
    } catch {
      // Use the system-role fallback below.
    }
  }
  if (authRole === "OPS") return "OPERATIONS_MANAGER";
  return "OPERATIONS_MANAGER";
}

export function setAdminSopRole(_roleId) {
  // Role is derived from the authenticated token; no client-side simulation.
}

export function useAdminPermissions() {
  const fallbackRoleId = resolveAdminRoleFromAuth();
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const role = String(api.getAuthRoleFromToken?.() || "").toUpperCase();
    if (role !== "ADMIN" && role !== "OPS") {
      setAccess(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    async function loadAccess() {
      try {
        const response = await api.getAdminAccess();
        if (!cancelled) setAccess(response?.access || response?.data?.access || null);
      } catch {
        if (!cancelled) setAccess(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    loadAccess();
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => {
    const roleId = access?.role?.id || fallbackRoleId;
    const role =
      ADMIN_ROLES[roleId] ||
      {
        id: roleId,
        label: access?.role?.name || roleId,
        can: access?.role?.permissionLabels || [],
        cannot: [],
      };
    const permissions = Array.isArray(access?.permissions) ? access.permissions : [];
    const hasServerPermissions = Boolean(access);
    const modules = hasServerPermissions
      ? Array.isArray(access?.modules) && access.modules.length
        ? access.modules
        : [...new Set(
            permissions
              .filter((permission) => permission === "*" || permission.includes("."))
              .map((permission) => (permission === "*" ? "*" : permission.split(".")[0]))
          )]
      : roleId === "SUPER_ADMIN"
        ? ROLE_MODULE_ACCESS.SUPER_ADMIN
        : loading
          ? []
          : ["dashboard"];

    function canAccessModule(permission) {
      if (roleId === "SUPER_ADMIN" || permissions.includes("*") || permissions.includes("access_all_modules") || modules.includes("*")) {
        return true;
      }
      return modules.includes(permission);
    }

    function hasPermission(permission) {
      if (roleId === "SUPER_ADMIN" || permissions.includes("*") || permissions.includes("access_all_modules")) {
        return true;
      }
      if (!hasServerPermissions) {
        const module = String(permission || "").split(".")[0];
        return modules.includes(module);
      }
      if (permissions.includes(permission)) return true;
      if (permission.includes(".")) {
        return modules.includes(permission.split(".")[0]);
      }
      return false;
    }

    function canPerform(action) {
      if (roleId === "SUPER_ADMIN" || permissions.includes("*") || permissions.includes("access_all_modules")) {
        return true;
      }
      const required = ACTION_REQUIRED_PERMISSION[action];
      if (!required) return true;
      if (!hasServerPermissions) return false;
      return required.some((permission) => permissions.includes(permission));
    }

    function cannotPerform(action) {
      return !canPerform(action);
    }

    return {
      roleId,
      role,
      modules,
      permissions,
      loading,
      isSuperAdmin: Boolean(access?.isSuperAdmin || roleId === "SUPER_ADMIN"),
      canAccessModule,
      hasPermission,
      canPerform,
      cannotPerform,
    };
  }, [access, fallbackRoleId, loading]);
}
